// =====================================================
// CAMPOS COMPARTIDOS DE ESTABLECIMIENTO (alta y edición) - BDSJEA-294
// src/pages/Agro/Productor/CamposEstablecimiento.js
// - Destino (catálogo de usos de lote)
// - Superficie: propia + arrendada manuales, total calculado (solo lectura)
// =====================================================

import React from "react"
import { Row, Col, Input } from "reactstrap"
import { Field, useFormikContext } from "formik"
import * as Yup from "yup"

import { useUsosLote } from "hooks/useUsosLote"
import NumberInput from "components/Input/NumberInput"
import { formatearNumero } from "helpers/formatters"

// Decimales del redondeo del cálculo del total (NO de la visualización).
export const DECIMALES_SUPERFICIE = 2
// BDSJEA-306 (reemplaza lo decidido en 304): las superficies se cargan con
// NumberInput entero (sin decimales); redondea a entero al salir del campo.
const DECIMALES_VISUALES = 0
export const SUPERFICIE_TOTAL_MINIMA_HA = 1

export const MSG_DESTINO_REQUERIDO = "Destino requerido"
export const MSG_SUPERFICIE_NUMERICA = "Debe ser un número"
export const MSG_SUPERFICIE_NEGATIVA = "No puede ser negativa"
export const MSG_SUPERFICIE_TOTAL_MINIMA = `La superficie total (propia + arrendada) debe ser como mínimo ${SUPERFICIE_TOTAL_MINIMA_HA} ha`
export const AYUDA_DESTINO_EDICION =
  "Cambiar el destino no modifica los lotes ya creados del establecimiento."

const redondear = num => {
  const factor = 10 ** DECIMALES_SUPERFICIE
  return Math.round(num * factor) / factor
}

const aNumero = valor => {
  if (valor === "" || valor === null || valor === undefined) return 0
  const num = Number(valor)
  return Number.isNaN(num) ? 0 : num
}

/** Total = propia + arrendada (vacío cuenta como 0), a 2 decimales. */
export const calcularSuperficieTotal = (propia, arrendada) =>
  redondear(aNumero(propia) + aNumero(arrendada))

const superficieParcial = () =>
  Yup.number()
    .transform((valor, original) => (original === "" ? undefined : valor))
    .typeError(MSG_SUPERFICIE_NUMERICA)
    .required("Superficie requerida")
    .min(0, MSG_SUPERFICIE_NEGATIVA)

/** Campos Yup de superficie + regla de total mínimo (se mezcla en el schema). */
export const superficieYupFields = {
  superficiePropiaHa: superficieParcial(),
  superficieArrendadaHa: superficieParcial().test(
    "total-minimo",
    MSG_SUPERFICIE_TOTAL_MINIMA,
    function (arrendada) {
      const { superficiePropiaHa } = this.parent
      return (
        calcularSuperficieTotal(superficiePropiaHa, arrendada) >=
        SUPERFICIE_TOTAL_MINIMA_HA
      )
    },
  ),
}

/** Combo Destino. Alta: sin default, placeholder "Seleccione". */
export const CampoDestino = ({ touched, errors, ayuda }) => {
  // BDSJEA-409: las opciones salen del catálogo del backend (TABLASGENERALES 146).
  const { usos, error } = useUsosLote()
  const { values } = useFormikContext()
  const actual = values?.destino
  // En edición el destino actual se muestra aunque el ítem ya no esté activo en el catálogo.
  const opciones =
    actual && !usos.some(u => u.value === actual)
      ? [...usos, { value: actual, label: actual }]
      : usos
  return (
    <div className="mb-3">
      <label className="form-label" htmlFor="destino">
        Destino *
      </label>
      <Field
        id="destino"
        name="destino"
        as="select"
        className={`form-control${touched.destino && errors.destino ? " is-invalid" : ""}`}
      >
        <option value="">Seleccione</option>
        {opciones.map(uso => (
          <option key={uso.value} value={uso.value}>
            {uso.label}
          </option>
        ))}
      </Field>
      {error && (
        <div className="text-danger small">
          No se pudieron cargar los destinos: {error}
        </div>
      )}
      {touched.destino && errors.destino && (
        <div className="text-danger small">{errors.destino}</div>
      )}
      {ayuda && <small className="text-muted">{ayuda}</small>}
    </div>
  )
}

/** Sección Superficie: propia y arrendada editables, total calculado. */
export const SeccionSuperficie = ({
  values,
  touched,
  errors,
  setFieldValue,
  setFieldTouched,
}) => {
  const total = calcularSuperficieTotal(
    values.superficiePropiaHa,
    values.superficieArrendadaHa,
  )

  // NumberInput solo llama a onChange al salir del campo (blur): se marca touched
  // sin validar y recién después se setea el valor (que dispara la validación).
  const campo = (name, etiqueta) => (
    <Col md={4}>
      <div className="mb-3">
        <label className="form-label" htmlFor={name}>
          {etiqueta}
        </label>
        <NumberInput
          id={name}
          name={name}
          aria-label={etiqueta}
          nroDecimales={0}
          value={values[name]}
          className={touched[name] && errors[name] ? "is-invalid" : undefined}
          onChange={num => {
            setFieldTouched(name, true, false)
            setFieldValue(name, num)
          }}
        />
        {touched[name] && errors[name] && (
          <div className="text-danger small">{errors[name]}</div>
        )}
      </div>
    </Col>
  )

  // Enter con foco en un campo de superficie: se fuerza el blur para que
  // NumberInput confirme el valor tipeado antes del submit implícito de Formik.
  const confirmarConEnter = e => {
    if (e.key === "Enter" && e.target.tagName === "INPUT") e.target.blur()
  }

  return (
    <Row onKeyDownCapture={confirmarConEnter}>
      {campo("superficiePropiaHa", "Superficie Propia (ha)")}
      {campo("superficieArrendadaHa", "Superficie Arrendada (ha)")}
      <Col md={4}>
        <div className="mb-3">
          <label className="form-label" htmlFor="superficieTotalCalculada">
            Superficie Total (ha)
          </label>
          <Input
            id="superficieTotalCalculada"
            type="text"
            className="text-end"
            disabled
            tabIndex={-1}
            value={formatearNumero(total, DECIMALES_VISUALES)}
          />
          <small className="text-muted">Propia + Arrendada</small>
        </div>
      </Col>
    </Row>
  )
}
